import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  FilePdf, DownloadSimple, Share, Link as LinkIcon, Trash, Image as ImageIcon,
  Buildings, Trophy, Sword, PersonSimpleRun, Coin, Megaphone, User, ChartBar,
  Ranking as RankingIcon, ChartLineUp, PencilSimple, FloppyDisk, Plus, X, ArrowCounterClockwise,
} from "@phosphor-icons/react";

const SECTION_ICONS = {
  clubs: Buildings,
  tournaments: Trophy,
  retas: Sword,
  dyd_run: PersonSimpleRun,
  dyd_coin: Coin,
  whatsapp: Megaphone,
  players: User,
  finance: ChartBar,
  ranking: RankingIcon,
  statistics: ChartLineUp,
};

const SECTION_HINTS = {
  clubs: "Listado de clubes o detalle con jugadores y canchas",
  tournaments: "Bracket, partidos y posiciones (selecciona uno específico)",
  retas: "Planillas, canchas, marcadores y ranking interno (selecciona una específica)",
  dyd_run: "Mis carreras + ranking semanal + retos",
  dyd_coin: "Movimientos de tu wallet",
  whatsapp: "Historial de campañas (solo admin)",
  players: "Perfil completo: stats, partidos, trofeos, DYD, runs (selecciona jugador)",
  finance: "Rentas + inscripciones (admin/directivo)",
  ranking: "Ranking calculado de partidos validados (filtra por fuente, club, categoría)",
  statistics: "KPIs globales: top scorers, win rate, distribución por categoría",
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Reportes() {
  const { user } = useAuth();
  const [allowed, setAllowed] = useState({});
  const [sections, setSections] = useState({});
  const [selected, setSelected] = useState(null);
  const [params, setParams] = useState({});
  const [generating, setGenerating] = useState(false);
  const [clubs, setClubs] = useState([]);
  const [logoOverride, setLogoOverride] = useState("");
  const [shares, setShares] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startHour, setStartHour] = useState("");
  const [endHour, setEndHour] = useState("");
  const [tournaments, setTournaments] = useState([]);
  const [retas, setRetas] = useState([]);
  const [players, setPlayers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [playerQ, setPlayerQ] = useState("");
  const selectedRef = useRef(null);

  // Stable callbacks (no deps on selected, use ref)
  const loadAccess = useCallback(async () => {
    try {
      const r = await api.get("/reports/my-access");
      const data = r.data || {};
      const allowedSet = {};
      (Array.isArray(data.allowed) ? data.allowed : []).forEach((k) => { allowedSet[k] = true; });
      setAllowed(allowedSet);
      setSections(data.sections && typeof data.sections === "object" ? data.sections : {});
      if (!selectedRef.current && Array.isArray(data.allowed) && data.allowed.length > 0) {
        selectedRef.current = data.allowed[0];
        setSelected(data.allowed[0]);
      }
    } catch (e) { toast.error("Error al cargar reportes"); }
  }, []);

  const loadClubs = useCallback(async () => {
    try {
      const r = await api.get("/clubs");
      const raw = r.data;
      const arr = Array.isArray(raw) ? raw : (Array.isArray(raw?.clubs) ? raw.clubs : []);
      setClubs(arr);
    } catch { setClubs([]); }
  }, []);

  const loadShares = useCallback(async () => {
    try {
      const r = await api.get("/reports/my-shares");
      const arr = Array.isArray(r.data?.shares) ? r.data.shares : [];
      setShares(arr);
    } catch { setShares([]); }
  }, []);

  // Selector loaders (filtered by club_id when applicable)
  const loadTournaments = useCallback(async (clubId) => {
    try {
      const qs = clubId ? `?club_id=${encodeURIComponent(clubId)}&limit=200` : "?limit=200";
      const r = await api.get(`/reports/selectors/tournaments${qs}`);
      setTournaments(Array.isArray(r.data?.items) ? r.data.items : []);
    } catch { setTournaments([]); }
  }, []);

  const loadRetas = useCallback(async (clubId) => {
    try {
      const qs = clubId ? `?club_id=${encodeURIComponent(clubId)}&limit=200` : "?limit=200";
      const r = await api.get(`/reports/selectors/retas${qs}`);
      setRetas(Array.isArray(r.data?.items) ? r.data.items : []);
    } catch { setRetas([]); }
  }, []);

  const loadPlayers = useCallback(async (clubId, q) => {
    try {
      const qs = [];
      if (clubId) qs.push(`club_id=${encodeURIComponent(clubId)}`);
      if (q) qs.push(`q=${encodeURIComponent(q)}`);
      qs.push("limit=200");
      const r = await api.get(`/reports/selectors/players?${qs.join("&")}`);
      setPlayers(Array.isArray(r.data?.items) ? r.data.items : []);
    } catch { setPlayers([]); }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const r = await api.get("/reports/selectors/categories");
      setCategories(Array.isArray(r.data?.items) ? r.data.items : []);
    } catch { setCategories([]); }
  }, []);

  useEffect(() => { loadAccess(); loadClubs(); loadShares(); loadCategories(); }, [loadAccess, loadClubs, loadShares, loadCategories]);

  // Refresh entity selectors when section changes or club filter changes
  useEffect(() => {
    if (!selected) return;
    if (selected === "tournaments" || selected === "ranking") loadTournaments(params.club_id || null);
    if (selected === "retas" || selected === "ranking") loadRetas(params.club_id || null);
    if (selected === "players") loadPlayers(params.club_id || null, "");
  }, [selected, params.club_id, loadTournaments, loadRetas, loadPlayers]);

  const handleUploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/reports/upload-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoOverride(r.data.url);
      toast.success("Logo subido");
    } catch (e) { toast.error("Error al subir"); }
    finally { setUploading(false); }
  };

  const generate = async (share = false) => {
    if (!selected) { toast.error("Selecciona un apartado"); return; }
    setGenerating(true);
    try {
      const mergedParams = { ...params };
      if (startDate) mergedParams.start_date = startDate;
      if (endDate) mergedParams.end_date = endDate;
      if (startHour !== "") mergedParams.start_hour = parseInt(startHour, 10);
      if (endHour !== "") mergedParams.end_hour = parseInt(endHour, 10);
      const body = {
        section: selected,
        params: mergedParams,
        share,
        logo_override_url: logoOverride || null,
      };
      if (share) {
        const r = await api.post("/reports/generate", body);
        toast.success(`Link compartible generado (válido ${r.data.expires_in_days} días)`);
        loadShares();
      } else {
        const r = await api.post("/reports/generate", body, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `${selected}_${new Date().toISOString().slice(0, 10)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("PDF descargado");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al generar PDF");
    } finally { setGenerating(false); }
  };

  const fullShareUrl = (shareId) => `${API_URL}/api/reports/shared/${shareId}`;
  const copyLink = (shareId) => { navigator.clipboard.writeText(fullShareUrl(shareId)); toast.success("Link copiado"); };
  const shareViaWhatsApp = (shareId, section) => {
    const text = encodeURIComponent(`Reporte ${sections[section] || section} · Disfruta y Diviértete: ${fullShareUrl(shareId)}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };
  const shareViaEmail = (shareId, section) => {
    const subject = encodeURIComponent(`Reporte ${sections[section] || section} · Disfruta y Diviértete`);
    const body = encodeURIComponent(`Te comparto este reporte:\n\n${fullShareUrl(shareId)}\n\n(Link válido 7 días)`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };
  const deleteShare = async (shareId) => {
    if (!window.confirm("¿Eliminar este link compartido?")) return;
    try { await api.delete(`/reports/shared/${shareId}`); toast.success("Eliminado"); loadShares(); }
    catch { toast.error("Error"); }
  };

  const sectionKeys = Object.keys(sections);

  return (
    <div className="space-y-6 animate-fade-in min-h-[400px]" data-testid="reportes-page">
      <div className="flex items-center gap-3">
        <FilePdf size={40} weight="fill" className="text-[#CCFF00]" />
        <div>
          <h1 className="text-3xl md:text-4xl font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white">Reportes PDF</h1>
          <p className="text-sm text-gray-400">Genera PDFs con logo del club y comparte por WhatsApp, email o link</p>
        </div>
      </div>

      {sectionKeys.length === 0 && (
        <div className="bg-white/5 border border-white/10 p-8 text-center text-gray-400" data-testid="empty-state">
          <FilePdf size={40} className="mx-auto mb-2 opacity-50" />
          <p>Cargando apartados...</p>
        </div>
      )}

      {sectionKeys.length > 0 && (
        <Tabs defaultValue="generator">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="generator" data-testid="tab-generator"><FilePdf size={14} className="mr-1" /> Generador</TabsTrigger>
            {["admin", "directivo"].includes(user?.role) && (
              <TabsTrigger value="editor" data-testid="tab-editor"><PencilSimple size={14} className="mr-1" /> Editor Manual</TabsTrigger>
            )}
          </TabsList>
          <TabsContent value="generator" className="mt-4 space-y-6">

      {sectionKeys.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="section-grid">
          {sectionKeys.map((key) => {
            const enabled = !!allowed[key];
            const Icon = SECTION_ICONS[key] || FilePdf;
            const isSelected = selected === key;
            return (
              <button
                key={key}
                disabled={!enabled}
                onClick={() => { setSelected(key); selectedRef.current = key; setParams({}); setPlayerQ(""); }}
                className={`p-4 border text-left transition-all ${
                  isSelected
                    ? "border-[#CCFF00] bg-[#CCFF00]/10"
                    : enabled
                      ? "border-white/10 bg-white/5 hover:border-[#CCFF00]/50"
                      : "border-white/5 bg-black/30 opacity-50 cursor-not-allowed"
                }`}
                data-testid={`section-${key}`}
              >
                <Icon size={24} className={isSelected ? "text-[#CCFF00]" : "text-gray-300"} />
                <div className="mt-2 font-bold text-white">{sections[key]}</div>
                <div className="text-[10px] text-gray-500 mt-1">{SECTION_HINTS[key]}</div>
                {!enabled && <Badge className="mt-2 bg-red-500/20 text-red-300 text-[10px]">Sin permiso</Badge>}
              </button>
            );
          })}
        </div>
      )}

      {selected && (
        <div className="bg-white/5 border border-white/10 p-5 space-y-4" data-testid="generator-panel">
          <h3 className="text-lg font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white">Generar: {sections[selected]}</h3>

          {(selected === "clubs" || selected === "finance" || selected === "tournaments" || selected === "retas" || selected === "ranking" || selected === "statistics" || selected === "players") && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-400">Club específico (opcional, vacío = todos)</Label>
              <Select value={params.club_id || "__all__"} onValueChange={(v) => setParams({ ...params, club_id: v === "__all__" ? null : v, tournament_id: null, reta_id: null, user_id: null })}>
                <SelectTrigger className="bg-black/50 border-white/10" data-testid="param-club"><SelectValue placeholder="Todos los clubes" /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/15">
                  <SelectItem value="__all__">Todos los clubes</SelectItem>
                  {clubs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {/* Logo del club seleccionado — se embebe en el PDF.
                  También resuelve el club desde la reta/torneo seleccionado para que el usuario vea EXACTAMENTE qué logo se embebe. */}
              {(() => {
                let club = clubs.find((c) => c.id === params.club_id);
                let source = club ? "club" : null;
                if (!club && params.reta_id) {
                  const reta = retas.find((r) => r.id === params.reta_id);
                  if (reta?.club_id) {
                    club = clubs.find((c) => c.id === reta.club_id);
                    source = "reta";
                  }
                }
                if (!club && params.tournament_id) {
                  const tour = tournaments.find((t) => t.id === params.tournament_id);
                  if (tour?.club_id) {
                    club = clubs.find((c) => c.id === tour.club_id);
                    source = "tournament";
                  }
                }
                if (!club) return null;
                const labelSrc = source === "reta" ? "Logo (resuelto de la reta seleccionada)" : source === "tournament" ? "Logo (resuelto del torneo seleccionado)" : "Logo a embeber";
                return (
                  <div className="mt-2 flex items-center gap-3 bg-black/40 border border-[#CCFF00]/20 p-2" data-testid="club-logo-preview">
                    {club.logo_url ? (
                      <img
                        src={club.logo_url.startsWith("http") ? club.logo_url : `${API_URL}${club.logo_url}`}
                        alt={club.name}
                        className="w-12 h-12 object-contain bg-white p-1 border border-white/10"
                      />
                    ) : (
                      <div className="w-12 h-12 flex items-center justify-center bg-[#CCFF00] text-black font-black text-xs border border-white/10">
                        {(club.name || "?").slice(0, 3).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs uppercase tracking-wider text-[#CCFF00] font-bold">{labelSrc}</p>
                      <p className="text-sm font-semibold text-white truncate">{club.name}</p>
                      <p className="text-[10px] text-gray-500">{club.logo_url ? "Logo registrado · se embebe en el PDF" : "Sin logo · se usa marca DYD por defecto"}</p>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Tournament selector */}
          {(selected === "tournaments" || selected === "ranking") && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-400">
                Torneo específico {selected === "tournaments" ? "(opcional, vacío = listado)" : "(opcional, filtra ranking)"}
              </Label>
              <Select value={params.tournament_id || "__all__"} onValueChange={(v) => setParams({ ...params, tournament_id: v === "__all__" ? null : v })}>
                <SelectTrigger className="bg-black/50 border-white/10" data-testid="param-tournament">
                  <SelectValue placeholder={tournaments.length ? "Selecciona un torneo" : "(sin torneos)"} />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/15 max-h-[300px]">
                  <SelectItem value="__all__">— Todos / Listado general —</SelectItem>
                  {tournaments.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} {t.club_name ? `· ${t.club_name}` : ""} {t.start_date ? `· ${t.start_date}` : ""} {t.status ? `· ${t.status}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Reta selector */}
          {(selected === "retas" || selected === "ranking") && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-400">
                Reta específica {selected === "retas" ? "(opcional, vacío = listado)" : "(opcional, filtra ranking)"}
              </Label>
              <Select value={params.reta_id || "__all__"} onValueChange={(v) => setParams({ ...params, reta_id: v === "__all__" ? null : v })}>
                <SelectTrigger className="bg-black/50 border-white/10" data-testid="param-reta">
                  <SelectValue placeholder={retas.length ? "Selecciona una reta" : "(sin retas)"} />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/15 max-h-[300px]">
                  <SelectItem value="__all__">— Todas / Listado general —</SelectItem>
                  {retas.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name || `Reta ${r.id?.slice(0, 8)}`} {r.club_name ? `· ${r.club_name}` : ""} {r.date ? `· ${r.date}` : ""} {r.type ? `· ${r.type}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Player selector (with search) */}
          {selected === "players" && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-gray-400">Jugador (opcional, vacío = tu perfil)</Label>
              <Input
                placeholder="Buscar por nombre, email o teléfono…"
                value={playerQ}
                onChange={(e) => {
                  setPlayerQ(e.target.value);
                  // Debounced search via simple timeout
                  clearTimeout(window.__rep_search_t);
                  window.__rep_search_t = setTimeout(() => loadPlayers(params.club_id || null, e.target.value), 250);
                }}
                className="bg-black/50 border-white/10"
                data-testid="player-search"
              />
              <Select value={params.user_id || "__self__"} onValueChange={(v) => setParams({ ...params, user_id: v === "__self__" ? null : v })}>
                <SelectTrigger className="bg-black/50 border-white/10" data-testid="param-player">
                  <SelectValue placeholder={players.length ? `${players.length} jugadores` : "(sin resultados)"} />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/15 max-h-[300px]">
                  <SelectItem value="__self__">— Mi propio perfil —</SelectItem>
                  {players.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} {p.category ? `· Cat ${p.category}` : ""} {p.role ? `· ${p.role}` : ""} {p.club_name ? `· ${p.club_name}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Category + source for ranking */}
          {(selected === "ranking" || selected === "statistics") && (
            <div className="grid sm:grid-cols-2 gap-3">
              {selected === "ranking" && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-400">Fuente</Label>
                  <Select value={params.source || "all"} onValueChange={(v) => setParams({ ...params, source: v })}>
                    <SelectTrigger className="bg-black/50 border-white/10" data-testid="param-source"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/15">
                      <SelectItem value="all">Todas (torneos + retas + rentas)</SelectItem>
                      <SelectItem value="tournament">Solo Torneos</SelectItem>
                      <SelectItem value="reta">Solo Retas</SelectItem>
                      <SelectItem value="rental">Solo Rentas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-400">Categoría (opcional)</Label>
                <Select value={params.category || "__all__"} onValueChange={(v) => setParams({ ...params, category: v === "__all__" ? null : v })}>
                  <SelectTrigger className="bg-black/50 border-white/10" data-testid="param-category"><SelectValue placeholder="Todas las categorías" /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/15">
                    <SelectItem value="__all__">Todas las categorías</SelectItem>
                    {categories.map((c) => <SelectItem key={c} value={c}>Cat {c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="bg-black/30 border border-white/10 p-3 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className="text-xs uppercase tracking-wider text-gray-400">
                Filtros opcionales · vacío = todo el histórico
              </Label>
              <Button variant="ghost" size="sm" onClick={() => { setStartDate(""); setEndDate(""); setStartHour(""); setEndHour(""); }} className="text-[10px] uppercase" data-testid="btn-clear-filters">
                Limpiar
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-gray-500">Desde fecha</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-black/60 border-white/10 mt-1 font-mono text-xs" data-testid="filter-start-date" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-gray-500">Hasta fecha</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-black/60 border-white/10 mt-1 font-mono text-xs" data-testid="filter-end-date" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-gray-500">Hora inicio (0-23)</Label>
                <Input type="number" min={0} max={23} value={startHour} onChange={(e) => setStartHour(e.target.value)} placeholder="6" className="bg-black/60 border-white/10 mt-1 font-mono text-xs" data-testid="filter-start-hour" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-gray-500">Hora fin (0-23)</Label>
                <Input type="number" min={0} max={23} value={endHour} onChange={(e) => setEndHour(e.target.value)} placeholder="22" className="bg-black/60 border-white/10 mt-1 font-mono text-xs" data-testid="filter-end-hour" />
              </div>
            </div>
            <p className="text-[10px] text-gray-500">Las horas son en UTC. Si el horario es nocturno (ej: 22 → 06), se acepta el wrap-around.</p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-400 flex items-center gap-2"><ImageIcon size={14} /> Logo (opcional)</Label>
            <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
              <Input value={logoOverride} onChange={(e) => setLogoOverride(e.target.value)} placeholder="URL del logo o sube archivo"
                className="bg-black/50 border-white/10 font-mono text-xs flex-1" data-testid="logo-url" />
              <input type="file" accept="image/*" onChange={(e) => handleUploadLogo(e.target.files?.[0])} disabled={uploading}
                className="text-sm text-gray-300 file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-[#CCFF00] file:text-black file:font-semibold file:uppercase file:tracking-wider"
                data-testid="logo-upload" />
            </div>
            <p className="text-[10px] text-gray-500 mt-1">Si no especificas logo, se usará el del club seleccionado.</p>
          </div>

          <div className="flex gap-3 flex-wrap">
            <Button onClick={() => generate(false)} disabled={generating} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="btn-download">
              <DownloadSimple size={16} className="mr-2" /> Descargar PDF
            </Button>
            <Button onClick={() => generate(true)} disabled={generating} variant="outline" data-testid="btn-share">
              <Share size={16} className="mr-2" /> Crear link compartible (7 días)
            </Button>
          </div>
        </div>
      )}

      {shares.length > 0 && (
        <div className="bg-white/5 border border-white/10 p-5 space-y-3" data-testid="shares-list">
          <h3 className="text-lg font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white">Mis links compartidos</h3>
          <div className="divide-y divide-white/10">
            {shares.map((s) => (
              <div key={s.share_id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`share-${s.share_id}`}>
                <FilePdf size={24} className="text-red-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white">{sections[s.section] || s.section}</div>
                  <div className="text-xs text-gray-500 truncate">
                    Expira: {new Date(s.expires_at).toLocaleString()} · ID: {s.share_id.slice(0, 8)}…
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => copyLink(s.share_id)} data-testid={`btn-copy-${s.share_id}`}>
                  <LinkIcon size={14} className="mr-1" /> Copiar
                </Button>
                <Button size="sm" variant="outline" onClick={() => shareViaWhatsApp(s.share_id, s.section)} className="bg-green-600/10 border-green-500/40 text-green-300 hover:bg-green-600/20" data-testid={`btn-wa-${s.share_id}`}>
                  WhatsApp
                </Button>
                <Button size="sm" variant="outline" onClick={() => shareViaEmail(s.share_id, s.section)} data-testid={`btn-email-${s.share_id}`}>
                  Email
                </Button>
                <Button size="sm" variant="ghost" onClick={() => window.open(fullShareUrl(s.share_id), "_blank")}>
                  Ver
                </Button>
                <Button size="sm" variant="ghost" onClick={() => deleteShare(s.share_id)} className="text-red-400">
                  <Trash size={14} />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {user?.role !== "admin" && (
        <p className="text-xs text-gray-500 text-center italic">
          ¿Necesitas acceso a más apartados? Pídele a un administrador que configure permisos en Plataforma → Reportes.
        </p>
      )}
          </TabsContent>
          {["admin", "directivo"].includes(user?.role) && (
            <TabsContent value="editor" className="mt-4">
              <AnnotationsEditor allowed={allowed} sections={sections} tournaments={tournaments} retas={retas} players={players} clubs={clubs} categories={categories} />
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  );
}

// ──────────────────── Editor Manual de Anotaciones ────────────────────

function AnnotationsEditor({ allowed, sections }) {
  const [section, setSection] = useState("");
  const [entityId, setEntityId] = useState("");
  const [entityList, setEntityList] = useState([]);
  const [text, setText] = useState("");
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [kpis, setKpis] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savedList, setSavedList] = useState([]);
  // v2 state
  const [images, setImages] = useState([]);
  const [expiresAt, setExpiresAt] = useState(null);
  const [expirationDays, setExpirationDays] = useState("");
  const [templates, setTemplates] = useState([]);
  const [versions, setVersions] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [waPhone, setWaPhone] = useState("");
  const [waIntro, setWaIntro] = useState("");
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const ENTITY_SECTIONS = {
    players: { label: "Jugador", endpoint: "/reports/selectors/players?limit=200" },
    tournaments: { label: "Torneo", endpoint: "/reports/selectors/tournaments?limit=200" },
    retas: { label: "Reta", endpoint: "/reports/selectors/retas?limit=200" },
  };

  const allowedEntries = Object.keys(allowed).filter(k => allowed[k]);

  const loadEntities = useCallback(async (sec) => {
    const cfg = ENTITY_SECTIONS[sec];
    if (!cfg) { setEntityList([]); return; }
    try {
      const r = await api.get(cfg.endpoint);
      setEntityList(Array.isArray(r.data?.items) ? r.data.items : []);
    } catch { setEntityList([]); }
  }, []);

  const loadSaved = useCallback(async () => {
    try {
      const r = await api.get("/reports/annotations");
      setSavedList(Array.isArray(r.data?.items) ? r.data.items : []);
    } catch { setSavedList([]); }
  }, []);

  useEffect(() => { loadSaved(); }, [loadSaved]);

  useEffect(() => {
    if (!section) return;
    loadEntities(section);
    setEntityId("");
  }, [section, loadEntities]);

  const loadAnnotation = useCallback(async (sec, eid) => {
    if (!sec) return;
    setLoading(true);
    try {
      const qs = eid ? `?entity_id=${encodeURIComponent(eid)}` : "";
      const r = await api.get(`/reports/annotations/${sec}${qs}`);
      setText(r.data.text || "");
      setNotes(Array.isArray(r.data.notes) ? r.data.notes : []);
      setKpis(Array.isArray(r.data.kpis) ? r.data.kpis : []);
      setImages(Array.isArray(r.data.images) ? r.data.images : []);
      setExpiresAt(r.data.expires_at || null);
      setMeta({ updated_at: r.data.updated_at, updated_by_name: r.data.updated_by_name, applied_template_name: r.data.applied_template_name });
    } catch { toast.error("Error al cargar"); }
    finally { setLoading(false); }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const r = await api.get("/reports/annotations-v2/templates");
      setTemplates(Array.isArray(r.data?.templates) ? r.data.templates : []);
    } catch { setTemplates([]); }
  }, []);

  const loadVersions = useCallback(async (sec, eid) => {
    if (!sec) return;
    try {
      const qs = eid ? `?entity_id=${encodeURIComponent(eid)}` : "";
      const r = await api.get(`/reports/annotations-v2/versions/${sec}${qs}`);
      setVersions(Array.isArray(r.data?.versions) ? r.data.versions : []);
    } catch { setVersions([]); }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  useEffect(() => {
    if (section) loadAnnotation(section, entityId);
  }, [section, entityId, loadAnnotation]);

  const save = async () => {
    if (!section) return toast.error("Selecciona una sección");
    setLoading(true);
    try {
      const r = await api.put(`/reports/annotations/${section}`, {
        entity_id: entityId || "", text, notes, kpis,
      });
      setMeta({ updated_at: r.data.updated_at, updated_by_name: r.data.updated_by_name });
      toast.success("Anotación guardada — se incrustará en el próximo PDF");
      loadSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  const remove = async () => {
    if (!section) return;
    if (!window.confirm("¿Eliminar esta anotación?")) return;
    try {
      const qs = entityId ? `?entity_id=${encodeURIComponent(entityId)}` : "";
      await api.delete(`/reports/annotations/${section}${qs}`);
      setText(""); setNotes([]); setKpis([]); setMeta(null);
      toast.success("Eliminada");
      loadSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const addNote = () => {
    if (!newNote.trim()) return;
    setNotes([...notes, newNote.trim()]);
    setNewNote("");
  };

  // ── V2 actions ──
  const uploadImage = async (file) => {
    if (!section || !file) return;
    const fd = new FormData();
    fd.append("section", section);
    fd.append("entity_id", entityId || "");
    fd.append("caption", file.name || "");
    fd.append("file", file);
    try {
      await api.post("/reports/annotations-v2/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Imagen subida");
      loadAnnotation(section, entityId);
    } catch (e) { toast.error(e.response?.data?.detail || "Error al subir"); }
  };

  const deleteImage = async (imgId) => {
    if (!section) return;
    if (!window.confirm("¿Eliminar esta imagen?")) return;
    try {
      const qs = entityId ? `?entity_id=${encodeURIComponent(entityId)}` : "";
      await api.delete(`/reports/annotations-v2/images/${section}/${imgId}${qs}`);
      loadAnnotation(section, entityId);
    } catch (e) { toast.error("Error al borrar"); }
  };

  const applyExpiration = async () => {
    if (!section) return;
    try {
      const days = expirationDays === "" ? null : parseInt(expirationDays);
      const r = await api.post(`/reports/annotations-v2/set-expiration/${section}`, {
        entity_id: entityId || "",
        days,
      });
      setExpiresAt(r.data.expires_at);
      toast.success(days ? `Expira en ${days} días` : "Expiración eliminada");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const applyTemplate = async (tplId) => {
    if (!section) return toast.error("Selecciona una sección primero");
    if (!window.confirm("Esto sobrescribirá la anotación actual. ¿Continuar? (se archivará versión)")) return;
    try {
      await api.post(`/reports/annotations-v2/templates/${tplId}/apply/${section}`, { entity_id: entityId || "" });
      toast.success("Plantilla aplicada");
      setShowTemplates(false);
      loadAnnotation(section, entityId);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const rollbackToVersion = async (versionId) => {
    if (!section) return;
    if (!window.confirm("¿Restaurar esta versión? La actual se guardará como nueva versión.")) return;
    try {
      await api.post(`/reports/annotations-v2/rollback/${section}/${versionId}`, { entity_id: entityId || "" });
      toast.success("Versión restaurada");
      setShowVersions(false);
      loadAnnotation(section, entityId);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const sendWhatsApp = async () => {
    if (!section) return;
    setSending(true);
    try {
      const payload = { entity_id: entityId || "", custom_intro: waIntro };
      if (waPhone.trim()) payload.phone = waPhone.trim();
      const r = await api.post(`/reports/annotations-v2/send-whatsapp/${section}`, payload);
      if (r.data?.result?.error) {
        toast.error(`WA rechazó: ${r.data.result.response?.error?.message || "error"}`);
      } else {
        toast.success(`Enviado a ${r.data.to}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSending(false); }
  };

  const generatePdfNow = async () => {
    if (!section) return;
    try {
      const params = {};
      if (entityId) {
        if (section === "players") params.user_id = entityId;
        else if (section === "tournaments") params.tournament_id = entityId;
        else if (section === "retas") params.reta_id = entityId;
      }
      const r = await api.post("/reports/generate", { section, params }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      toast.success("PDF abierto");
    } catch { toast.error("Error al generar"); }
  };

  const entityCfg = ENTITY_SECTIONS[section];

  return (
    <div className="grid lg:grid-cols-[1fr,320px] gap-4" data-testid="annotations-editor">
      <div className="space-y-4">
        <div className="bg-black/40 border border-white/10 p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-400">Sección</Label>
              <Select value={section} onValueChange={setSection}>
                <SelectTrigger className="bg-black/50 border-white/10" data-testid="ann-section"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/15">
                  {allowedEntries.map(k => <SelectItem key={k} value={k}>{sections[k] || k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {entityCfg && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-400">{entityCfg.label} (opcional)</Label>
                <Select value={entityId || "__all__"} onValueChange={(v) => setEntityId(v === "__all__" ? "" : v)}>
                  <SelectTrigger className="bg-black/50 border-white/10" data-testid="ann-entity"><SelectValue placeholder={entityList.length ? `${entityList.length} disponibles` : "(sin opciones)"} /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/15 max-h-[300px]">
                    <SelectItem value="__all__">— General —</SelectItem>
                    {entityList.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name || e.id.slice(0, 8)}
                        {e.club_name ? ` · ${e.club_name}` : ""}
                        {e.start_date || e.date ? ` · ${e.start_date || e.date}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          {meta?.updated_at && (
            <div className="text-[10px] text-gray-500">
              Última edición: {meta.updated_at.slice(0, 16).replace("T", " ")} por <span className="text-gray-300">{meta.updated_by_name || "—"}</span>
            </div>
          )}
        </div>

        {section && (
          <>
            <div className="bg-black/40 border border-white/10 p-5 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-gray-400">Notas (párrafos) · soporta Markdown</Label>
                <span className="text-[10px] text-gray-500 italic">**negrita** _cursiva_ `código` # H1 ## H2 ### H3</span>
              </div>
              <Textarea
                rows={7}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="# Título principal&#10;&#10;Contexto **importante** y _observaciones_.&#10;&#10;Doble Enter = párrafo nuevo."
                className="bg-black/50 border-white/10 font-mono text-sm"
                data-testid="ann-text"
              />
              <div className="text-[10px] text-gray-500">{text.length}/8000 caracteres</div>
            </div>

            <div className="bg-black/40 border border-white/10 p-5 space-y-3">
              <Label className="text-xs uppercase tracking-wider text-gray-400">Puntos clave (bullets)</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Ej. 'Aumentar resistencia 2x/sem'"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNote(); } }}
                  className="bg-black/50 border-white/10"
                  data-testid="ann-newnote"
                />
                <Button onClick={addNote} size="sm" className="bg-[#CCFF00] text-black hover:bg-[#CCFF00]/80" data-testid="ann-addnote">
                  <Plus size={14} />
                </Button>
              </div>
              <ul className="space-y-1">
                {notes.map((n, i) => (
                  <li key={i} className="flex items-center justify-between gap-2 bg-black/30 border border-white/5 px-3 py-2 text-sm text-white" data-testid={`ann-note-${i}`}>
                    <span>• {n}</span>
                    <Button size="sm" variant="ghost" onClick={() => setNotes(notes.filter((_, j) => j !== i))} className="text-red-400 h-6 w-6 p-0">
                      <X size={12} />
                    </Button>
                  </li>
                ))}
                {notes.length === 0 && <li className="text-xs text-gray-500 italic">Sin puntos añadidos.</li>}
              </ul>
            </div>

            <div className="bg-black/40 border border-white/10 p-5 space-y-3">
              <Label className="text-xs uppercase tracking-wider text-gray-400">Cifras adicionales (hasta 6 KPIs)</Label>
              <div className="grid sm:grid-cols-2 gap-2">
                {kpis.map((k, i) => (
                  <div key={i} className="flex items-center gap-2 bg-black/30 border border-white/5 p-2">
                    <Input
                      placeholder="Etiqueta"
                      value={k.label}
                      onChange={(e) => { const c = [...kpis]; c[i] = { ...c[i], label: e.target.value }; setKpis(c); }}
                      className="bg-black/50 border-white/10 h-8 text-xs"
                      data-testid={`ann-kpi-label-${i}`}
                    />
                    <Input
                      placeholder="Valor"
                      value={k.value}
                      onChange={(e) => { const c = [...kpis]; c[i] = { ...c[i], value: e.target.value }; setKpis(c); }}
                      className="bg-black/50 border-white/10 h-8 text-xs"
                      data-testid={`ann-kpi-value-${i}`}
                    />
                    <Button size="sm" variant="ghost" onClick={() => setKpis(kpis.filter((_, j) => j !== i))} className="text-red-400 h-8 w-8 p-0">
                      <X size={12} />
                    </Button>
                  </div>
                ))}
                {kpis.length < 6 && (
                  <Button size="sm" variant="outline" onClick={() => setKpis([...kpis, { label: "", value: "" }])} className="border-dashed border-white/20 text-gray-400" data-testid="ann-add-kpi">
                    <Plus size={14} className="mr-1" /> Añadir KPI
                  </Button>
                )}
              </div>
            </div>

            {/* ── V2: Imágenes ── */}
            <div className="bg-black/40 border border-white/10 p-5 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-gray-400">📷 Imágenes adjuntas ({images.length}/5)</Label>
                <Button
                  size="sm" variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={images.length >= 5}
                  data-testid="ann-upload-image-btn"
                >
                  <ImageIcon size={14} className="mr-1" /> Subir
                </Button>
                <input
                  ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(f); e.target.value = ""; }}
                />
              </div>
              {images.length === 0 ? (
                <div className="text-xs text-gray-500 italic">Sin imágenes. PNG, JPEG o WebP, máx 2MB cada una.</div>
              ) : (
                <div className="grid sm:grid-cols-3 gap-2">
                  {images.map((img) => (
                    <div key={img.id} className="bg-black/30 border border-white/10 p-2 space-y-1" data-testid={`ann-image-${img.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-gray-400 truncate flex-1">{img.filename}</span>
                        <Button size="sm" variant="ghost" onClick={() => deleteImage(img.id)} className="text-red-400 h-6 w-6 p-0"><X size={11} /></Button>
                      </div>
                      <div className="text-[10px] text-gray-500">{Math.round((img.size || 0) / 1024)}KB · {img.content_type?.split("/")[1]}</div>
                      {img.caption && <div className="text-[10px] text-gray-300 truncate">"{img.caption}"</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── V2: Expiración + Plantillas + Versiones ── */}
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="bg-black/40 border border-white/10 p-4 space-y-2">
                <Label className="text-xs uppercase tracking-wider text-gray-400">⏰ Expiración</Label>
                {expiresAt ? (
                  <div className="text-xs text-amber-300">Expira: {expiresAt.slice(0, 10)}</div>
                ) : (
                  <div className="text-xs text-gray-500 italic">Sin expiración</div>
                )}
                <div className="flex gap-1">
                  <Input
                    type="number" placeholder="días (vacío=quitar)"
                    value={expirationDays}
                    onChange={(e) => setExpirationDays(e.target.value)}
                    className="bg-black/50 border-white/10 h-8 text-xs"
                    data-testid="ann-expiration-days"
                  />
                  <Button size="sm" onClick={applyExpiration} className="bg-amber-500/20 hover:bg-amber-500/40 text-amber-300 border border-amber-500/30 h-8" data-testid="ann-set-expiration">
                    OK
                  </Button>
                </div>
              </div>

              <div className="bg-black/40 border border-white/10 p-4 space-y-2">
                <Label className="text-xs uppercase tracking-wider text-gray-400">📋 Plantillas</Label>
                <div className="text-xs text-gray-500">{templates.length} disponibles</div>
                <Button size="sm" variant="outline" onClick={() => setShowTemplates(true)} className="w-full h-8 text-xs" data-testid="ann-templates-btn">
                  Aplicar plantilla
                </Button>
                {meta?.applied_template_name && (
                  <div className="text-[10px] text-cyan-300 italic">Aplicada: {meta.applied_template_name}</div>
                )}
              </div>

              <div className="bg-black/40 border border-white/10 p-4 space-y-2">
                <Label className="text-xs uppercase tracking-wider text-gray-400">📚 Historial</Label>
                <Button size="sm" variant="outline" onClick={() => { loadVersions(section, entityId); setShowVersions(true); }} className="w-full h-8 text-xs" data-testid="ann-versions-btn">
                  Ver versiones
                </Button>
              </div>
            </div>

            {/* ── V2: WhatsApp ── */}
            <div className="bg-green-500/5 border border-green-500/20 p-4 space-y-2">
              <Label className="text-xs uppercase tracking-wider text-green-300">📱 Enviar por WhatsApp al jugador</Label>
              <div className="grid sm:grid-cols-2 gap-2">
                <Input
                  placeholder="Teléfono (vacío = usar el del jugador)"
                  value={waPhone}
                  onChange={(e) => setWaPhone(e.target.value)}
                  className="bg-black/50 border-white/10 h-9 text-xs"
                  data-testid="ann-wa-phone"
                />
                <Input
                  placeholder="Saludo personalizado (opcional)"
                  value={waIntro}
                  onChange={(e) => setWaIntro(e.target.value)}
                  className="bg-black/50 border-white/10 h-9 text-xs"
                  data-testid="ann-wa-intro"
                />
              </div>
              <Button onClick={sendWhatsApp} disabled={sending || !section} className="bg-green-500/30 hover:bg-green-500/50 text-green-200 border border-green-500/30" data-testid="ann-wa-send">
                <Megaphone size={14} className="mr-1" /> {sending ? "Enviando..." : "Enviar ahora"}
              </Button>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <Button onClick={save} disabled={loading} className="bg-[#CCFF00] text-black hover:bg-[#CCFF00]/80" data-testid="ann-save">
                <FloppyDisk size={16} className="mr-1" /> Guardar
              </Button>
              <Button onClick={generatePdfNow} variant="outline" data-testid="ann-preview">
                <FilePdf size={16} className="mr-1" /> Previsualizar PDF
              </Button>
              {meta?.updated_at && (
                <Button onClick={remove} variant="outline" className="text-red-400 border-red-500/30 ml-auto" data-testid="ann-delete">
                  <Trash size={14} className="mr-1" /> Eliminar
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white">
          Guardadas · {savedList.length}
        </h3>
        <div className="space-y-2 max-h-[600px] overflow-auto pr-1">
          {savedList.length === 0 && <div className="text-xs text-gray-500 italic">Aún no hay anotaciones.</div>}
          {savedList.map((a) => (
            <button
              key={`${a.section}_${a.entity_id || "general"}`}
              type="button"
              onClick={() => { setSection(a.section); setEntityId(a.entity_id || ""); }}
              className="w-full text-left bg-black/30 border border-white/10 hover:border-white/30 p-3 transition-all"
              data-testid={`ann-saved-${a.section}-${a.entity_id || "general"}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-white font-medium">{sections[a.section] || a.section}</span>
                <Badge variant="outline" className="text-[9px]">{a.entity_id ? a.entity_id.slice(0, 8) : "general"}</Badge>
              </div>
              <div className="text-[10px] text-gray-400 line-clamp-2 mt-1">
                {(a.text || "").slice(0, 100) || `${(a.notes || []).length} pts · ${(a.kpis || []).length} KPIs`}
              </div>
              <div className="text-[9px] text-gray-500 mt-1">
                {a.updated_at?.slice(0, 16).replace("T", " ")} · {a.updated_by_name}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* ── Templates Modal ── */}
      {showTemplates && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowTemplates(false)} data-testid="templates-modal">
          <div className="bg-[#0A0A0A] border border-white/15 max-w-2xl w-full max-h-[80vh] overflow-auto p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-['Barlow_Condensed'] font-black uppercase text-white">Plantillas disponibles</h3>
              <Button size="sm" variant="ghost" onClick={() => setShowTemplates(false)}><X size={16} /></Button>
            </div>
            <TemplateCreator onCreated={loadTemplates} />
            <div className="space-y-2">
              {templates.length === 0 && <div className="text-xs text-gray-500 italic">No hay plantillas todavía.</div>}
              {templates.map((t) => (
                <div key={t.id} className="bg-black/30 border border-white/10 p-3 flex items-center justify-between gap-2" data-testid={`template-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white font-medium">{t.name}</div>
                    <div className="text-[10px] text-gray-400 truncate">{t.description}</div>
                    <div className="text-[9px] text-gray-500">{(t.notes || []).length} notas · {(t.kpis || []).length} KPIs · creada {t.created_at?.slice(0, 10)}</div>
                  </div>
                  <Button size="sm" onClick={() => applyTemplate(t.id)} className="bg-[#CCFF00] text-black hover:bg-[#CCFF00]/80" data-testid={`apply-template-${t.id}`}>
                    Aplicar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={async () => {
                    if (!window.confirm("¿Eliminar plantilla?")) return;
                    try { await api.delete(`/reports/annotations-v2/templates/${t.id}`); loadTemplates(); toast.success("Eliminada"); }
                    catch { toast.error("Error"); }
                  }} className="text-red-400"><Trash size={14} /></Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Versions Modal ── */}
      {showVersions && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowVersions(false)} data-testid="versions-modal">
          <div className="bg-[#0A0A0A] border border-white/15 max-w-2xl w-full max-h-[80vh] overflow-auto p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-['Barlow_Condensed'] font-black uppercase text-white">Historial de versiones · {versions.length}</h3>
              <Button size="sm" variant="ghost" onClick={() => setShowVersions(false)}><X size={16} /></Button>
            </div>
            {versions.length === 0 && <div className="text-xs text-gray-500 italic">No hay versiones archivadas todavía.</div>}
            <div className="space-y-2">
              {versions.map((v) => (
                <div key={v.id} className="bg-black/30 border border-white/10 p-3 space-y-1" data-testid={`version-${v.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-white"><Badge variant="outline" className="text-[10px] mr-2">v{v.version_n}</Badge>{v.archived_at?.slice(0, 16).replace("T", " ")} por {v.archived_by_name}</span>
                    <Button size="sm" variant="outline" onClick={() => rollbackToVersion(v.id)} className="text-[#CCFF00] border-[#CCFF00]/30" data-testid={`rollback-${v.id}`}>
                      <ArrowCounterClockwise size={12} className="mr-1" /> Restaurar
                    </Button>
                  </div>
                  <div className="text-[10px] text-gray-400 italic">{v.reason || "—"}</div>
                  <div className="text-[10px] text-gray-300 line-clamp-2">{((v.doc || {}).text || "").slice(0, 150)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Inline template creator used inside the modal
function TemplateCreator({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [text, setText] = useState("");
  const create = async () => {
    if (!name.trim()) return toast.error("Pon un nombre");
    try {
      await api.post("/reports/annotations-v2/templates", {
        name, description, text,
        notes: [], kpis: [],
      });
      toast.success("Plantilla creada");
      setName(""); setDescription(""); setText(""); setOpen(false);
      onCreated?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  if (!open) return (
    <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="border-dashed border-white/20 text-gray-300 w-full" data-testid="template-create-btn">
      <Plus size={14} className="mr-1" /> Nueva plantilla
    </Button>
  );
  return (
    <div className="bg-black/30 border border-cyan-500/30 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wider text-cyan-300">Nueva plantilla</Label>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}><X size={14} /></Button>
      </div>
      <Input placeholder="Nombre (ej. Evaluación Trimestral)" value={name} onChange={(e) => setName(e.target.value)} className="bg-black/50 border-white/10 h-8 text-xs" data-testid="template-name" />
      <Input placeholder="Descripción" value={description} onChange={(e) => setDescription(e.target.value)} className="bg-black/50 border-white/10 h-8 text-xs" data-testid="template-description" />
      <Textarea rows={4} placeholder="Texto base. Usa {{date}} {{quarter}} {{month}} {{user_name}} como variables." value={text} onChange={(e) => setText(e.target.value)} className="bg-black/50 border-white/10 text-xs font-mono" data-testid="template-text" />
      <Button size="sm" onClick={create} className="bg-cyan-500/30 text-cyan-200 border border-cyan-500/30" data-testid="template-save">Crear</Button>
    </div>
  );
}
